import styled, { keyframes } from "styled-components";
import { Text } from "../../components/common/texts";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

export const TitleBlock = styled.div`
  text-align: center;
  margin-bottom: 24px;

  h2 {
    position: relative;
    display: inline-block;
    padding-bottom: 14px;

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 70px;
      height: 3px;
      border-radius: 3px;
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const IntroWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto 30px auto;
  text-align: center;
`;

export const IntroText = styled(Text)`
  white-space: pre-line;
  text-align: center;
  line-height: 1.9;
  color: #444;

  strong {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
  }
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 40px auto 50px auto;
  max-width: 360px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      ${({ theme }) => theme.colors.primary},
      transparent
    );
  }

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px rgba(197, 161, 21, 0.15);
    flex-shrink: 0;
  }
`;

export const ExpertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 28px;
  perspective: 1600px;
`;

export const ExpertCard = styled.article`
  position: relative;
  background: transparent;
  min-height: 420px;
  opacity: 0;
  cursor: pointer;
  transition: transform 0.35s ease;

  &.is-visible {
    animation: ${fadeUp} 0.7s ease forwards;
    animation-delay: ${({ $delay }) => $delay || 0}ms;
  }

  &:hover {
    transform: translateY(-8px);
  }

  @media (max-width: 480px) {
    min-height: 380px;
  }
`;

export const FlipInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: inherit;
  transition: transform 0.85s cubic-bezier(0.6, 0.05, 0.28, 0.91);
  transform-style: preserve-3d;

  ${ExpertCard}:hover &,
  ${ExpertCard}.is-flipped & {
    transform: rotateY(180deg);
  }
`;

const faceBase = `
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 18px;
  padding: 36px 24px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.35s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      #c5a115,
      #f5d76e,
      #c5a115
    );
    background-size: 200% 100%;
    transition: background-position 0.6s ease;
  }

  ${ExpertCard}:hover & {
    box-shadow: 0 16px 36px rgba(197, 161, 21, 0.18);

    &::before {
      background-position: 100% 0;
    }
  }
`;

export const FaceFront = styled.div`
  ${faceBase}
  justify-content: flex-start;

  @media (max-width: 480px) {
    padding: 28px 20px 22px;
  }
`;

export const FaceBack = styled.div`
  ${faceBase}
  transform: rotateY(180deg);
  justify-content: center;
  background: linear-gradient(155deg, #fffaf0 0%, #fff 55%, #fdf3d4 100%);
`;

export const AvatarRing = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  margin-bottom: 20px;
  border-radius: 50%;
  padding: 4px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary},
    #f5d76e
  );
  box-shadow: 0 6px 18px rgba(197, 161, 21, 0.25);

  @media (max-width: 480px) {
    width: 118px;
    height: 118px;
  }
`;

export const Avatar = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  filter: grayscale(20%);
  transition: filter 0.4s ease, transform 0.5s ease;

  ${ExpertCard}:hover & {
    filter: grayscale(0%);
    transform: scale(1.04);
  }
`;

export const ExpertName = styled.h3`
  font-size: var(--normal-text);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 12px;
  text-align: center;
`;

export const ExpertRole = styled.p`
  font-size: var(--small-text);
  color: #555;
  line-height: 1.7;
  white-space: pre-line;
  text-align: center;
  margin: 0;
`;

export const BackName = styled.h3`
  font-size: var(--normal-text);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 22px;
  position: relative;
  padding-bottom: 12px;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.primary},
      #f5d76e
    );
  }
`;

const linkPop = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const LinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 230px;
`;

export const LinkItem = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  border-radius: 999px;
  background: #fff;
  color: #333;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid rgba(197, 161, 21, 0.25);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  opacity: 0;
  transition: transform 0.3s ease, box-shadow 0.3s ease,
    background 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  ${ExpertCard}:hover &,
  ${ExpertCard}.is-flipped & {
    animation: ${linkPop} 0.5s ease forwards;
    animation-delay: ${({ $delay }) => $delay || 0}ms;
  }

  svg {
    flex-shrink: 0;
    font-size: 1.1rem;
    color: ${({ $color }) => $color || "#c5a115"};
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(197, 161, 21, 0.22);
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;

    svg {
      color: #fff;
      transform: scale(1.1) rotate(-6deg);
    }
  }
`;
